import * as Sentry from "@sentry/react";

/**
 * Available log levels.
 */
enum LogLevel {
    INFO = 'info',
    WARN = 'warn',
    ERROR = 'error',
}

/**
 * Logs a message.
 * @param {LogLevel} level Log level.
 * @param {string} message Message.
 * @param {Record<string, any>} context Optional context.
 */
function logMessage(level: LogLevel, message: string, context: Record<string, any> = {}) {
    const time = new Date().toISOString();

    const logMessage = {
        time,
        level,
        message,
        context
    }

    switch (level) {
        case LogLevel.INFO:
            console.info(logMessage);
            break;
        case LogLevel.WARN:
            console.warn(logMessage);
            Sentry.captureMessage(message, {
                level: 'warning',
                extra: {
                    ...context
                }
            });
            break;
        case LogLevel.ERROR:
            console.error(logMessage);
            Sentry.captureException(context.error || new Error(message), {
                level: 'error',
                extra: {
                    ...context
                }
            });
            break;
        default:
            console.log(logMessage);
    }
}

class Logger {
    /**
     * Logs info message.
     * @param {string} message Log message.
     * @param {Record<string,any>} context Optional context.
     * @returns {void}
     */
    info(message: string, context?: Record<string, any>): void {
        return logMessage(LogLevel.INFO, message, context);
    }

    /**
     * Logs warning message.
     * @param {string} message Log message.
     * @param {Record<string,any>} context Optional context.
     * @returns {void}
     */
    warn(message: string, context?: Record<string, any>): void {
        return logMessage(LogLevel.WARN, message, context);
    }

    /**
     * Logs error message.
     * @param {string} message Log message.
     * @param {Record<string,any>} context Optional context.
     * @returns {void}
     */
    error(message: string, context?: Record<string, any>): void {
        return logMessage(LogLevel.ERROR, message, context);
    }

    /**
     * Logs unknown error if it is an Error.
     * @param {unknown} error An unknown error.
     * @param {Record<string, any>} context Optional context.
     */
    logIfError(error: unknown, context?: Record<string, any>): void {
        if (error instanceof Error) {
            this.error(error.message, {
                ...context,
                error: error
            });
        }
    }
}

export const logger = new Logger();
